import { Controller } from "@hotwired/stimulus"

/**
 * RT Welds Controller
 * Manages single-entry weld creation/editing with customizable labels
 * Handles weld CRUD operations for RT reports
 * Links welds to exposure setups
 */
export default class extends Controller {
  static targets = [
    "weldId",
    "reportId",
    "taskId",
    "area",
    "weldNo",
    "diameter",
    "wallThickness",
    "imageRef",
    "welderIds",
    "radiographyExposureId",
    "saveButton",
    "saveButtonText",
    "alertBox",
    "weldsContainer"
  ]

  connect() {
    console.log('[rt-welds] Controller connected')
    this.isEditMode = false
    this.weldIdToDelete = null
  }

  /**
   * Save or update weld
   */
  async saveWeld(event) {
    event.preventDefault()

    console.log('[rt-welds] Save weld clicked')

    // Validate required fields
    if (!this.validateFields()) {
      return
    }

    // Collect form data
    const weldData = this.collectFormData()

    console.log('[rt-welds] Weld data:', weldData)

    // Disable save button during request
    this.saveButtonTarget.disabled = true
    this.saveButtonTextTarget.textContent = this.isEditMode ? 'Updating...' : 'Saving...'

    try {
      const url = this.isEditMode
        ? `/reports/radiography/tasks/${this.taskIdTarget.value}/reports/${this.reportIdTarget.value}/welds/${this.weldIdTarget.value}`
        : `/reports/radiography/tasks/${this.taskIdTarget.value}/reports/${this.reportIdTarget.value}/welds`

      const method = this.isEditMode ? 'PATCH' : 'POST'

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ weld: weldData })
      })

      const data = await response.json()

      if (response.ok && data.status === 'success') {
        console.log('[rt-welds] Weld saved successfully:', data)
        this.handleSaveSuccess(data)
      } else {
        console.error('[rt-welds] Save failed:', data)
        this.showError(data.message || 'Failed to save weld')
      }
    } catch (error) {
      console.error('[rt-welds] Network error:', error)
      this.showError('Network error. Please try again.')
    } finally {
      this.saveButtonTarget.disabled = false
      this.saveButtonTextTarget.textContent = this.isEditMode ? 'Update Weld' : 'Save Weld'
    }
  }

  /**
   * Edit existing weld
   */
  async editWeld(event) {
    event.preventDefault()

    const weldId = event.currentTarget.dataset.weldId

    console.log('[rt-welds] Edit weld:', weldId)

    try {
      const response = await fetch(
        `/reports/radiography/tasks/${this.taskIdTarget.value}/reports/${this.reportIdTarget.value}/welds/${weldId}.json`
      )

      if (!response.ok) throw new Error('Failed to fetch weld data')

      const data = await response.json()

      console.log('[rt-welds] Weld data loaded:', data)

      // Populate form fields
      this.populateForm(data)

      // Set edit mode
      this.isEditMode = true
      this.weldIdTarget.value = weldId
      this.saveButtonTextTarget.textContent = 'Update Weld'

      // Scroll to form
      this.element.querySelector('.card').scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (error) {
      console.error('[rt-welds] Error loading weld:', error)
      this.showError('Failed to load weld data')
    }
  }

  /**
   * Delete weld - show confirmation modal
   */
  deleteWeld(event) {
    event.preventDefault()

    const weldId = event.currentTarget.dataset.weldId
    this.weldIdToDelete = weldId

    console.log('[rt-welds] Showing delete confirmation for weld:', weldId)

    // Show modal
    const modal = document.getElementById('deleteWeldModal')
    if (modal) {
      if (window.jQuery) {
        window.jQuery(modal).modal('show')
      } else {
        modal.classList.add('show')
        modal.style.display = 'block'

        const backdrop = document.createElement('div')
        backdrop.className = 'modal-backdrop fade show'
        backdrop.id = 'deleteWeldBackdrop'
        document.body.appendChild(backdrop)
        document.body.classList.add('modal-open')
      }
    }
  }

  /**
   * Confirm delete weld
   */
  async confirmDeleteWeld(event) {
    event.preventDefault()

    if (!this.weldIdToDelete) {
      console.error('[rt-welds] No weld ID to delete')
      return
    }

    const weldId = this.weldIdToDelete

    console.log('[rt-welds] Deleting weld:', weldId)

    // Close modal
    this.closeModal('deleteWeldModal', 'deleteWeldBackdrop')

    try {
      const response = await fetch(
        `/reports/radiography/tasks/${this.taskIdTarget.value}/reports/${this.reportIdTarget.value}/welds/${weldId}`,
        {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          }
        }
      )

      const data = await response.json()

      if (response.ok && data.status === 'success') {
        console.log('[rt-welds] Weld deleted successfully')

        // Remove the table row from DOM
        const weldRow = this.weldsContainerTarget.querySelector(`tr[data-weld-id="${weldId}"]`)
        if (weldRow) {
          weldRow.remove()
        }

        // Check if no welds left, show empty state
        const remainingRows = this.weldsContainerTarget.querySelectorAll('tbody tr')
        if (remainingRows.length === 0) {
          this.showEmptyState()
        }

        // Clear the stored ID
        this.weldIdToDelete = null
      } else {
        console.error('[rt-welds] Delete failed:', data)
        this.showError(data.message || 'Failed to delete weld')
      }
    } catch (error) {
      console.error('[rt-welds] Delete error:', error)
      this.showError('Network error. Please try again.')
    }
  }

  /**
   * Cancel edit mode
   */
  cancelEdit(event) {
    event.preventDefault()

    console.log('[rt-welds] Cancel edit')

    this.clearForm()
    this.isEditMode = false
    this.weldIdTarget.value = ''
    this.saveButtonTextTarget.textContent = 'Save Weld'
  }

  /**
   * Edit optional header title
   */
  editHeaderTitle(event) {
    event.preventDefault()

    console.log('[rt-welds] Edit header title')

    // Show modal
    const modal = document.getElementById('editHeaderTitleModal')
    if (modal) {
      if (window.jQuery) {
        window.jQuery(modal).modal('show')
      } else {
        modal.classList.add('show')
        modal.style.display = 'block'

        const backdrop = document.createElement('div')
        backdrop.className = 'modal-backdrop fade show'
        backdrop.id = 'editHeaderTitleBackdrop'
        document.body.appendChild(backdrop)
        document.body.classList.add('modal-open')
      }
    }
  }

  /**
   * Save new header title
   */
  async saveHeaderTitle(event) {
    event.preventDefault()

    const input = document.getElementById('new-header-title-input')
    const newTitle = input ? input.value.trim() : ''

    if (!newTitle) {
      alert('Please enter a header title')
      return
    }

    console.log('[rt-welds] Save header title:', newTitle)

    try {
      const response = await fetch(
        `/tasks/${this.taskIdTarget.value}/reports/${this.reportIdTarget.value}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            report: {
              optional_header_title: newTitle
            }
          })
        }
      )

      if (response.ok) {
        console.log('[rt-welds] Header title updated')

        // Close modal
        this.closeModal('editHeaderTitleModal', 'editHeaderTitleBackdrop')

        // Reload page to update all labels
        window.location.reload()
      } else {
        this.showError('Failed to update header title')
      }
    } catch (error) {
      console.error('[rt-welds] Error updating header title:', error)
      this.showError('Network error. Please try again.')
    }
  }


  /**
   * Close modal helper
   */
  closeModal(modalId, backdropId) {
    const modal = document.getElementById(modalId)
    if (modal) {
      if (window.jQuery) {
        window.jQuery(modal).modal('hide')
      } else {
        modal.classList.remove('show')
        modal.style.display = 'none'

        const backdrop = document.getElementById(backdropId)
        if (backdrop) {
          backdrop.remove()
        }
        document.body.classList.remove('modal-open')
      }
    }
  }

  /**
   * Collect form data
   */
  collectFormData() {
    // Get welder IDs from Select2
    let welderIds = []
    if (this.welderIdsTarget.tomselect) {
      welderIds = this.welderIdsTarget.tomselect.getValue()
    } else if (window.jQuery && window.jQuery(this.welderIdsTarget).data('select2')) {
      welderIds = window.jQuery(this.welderIdsTarget).val() || []
    } else {
      welderIds = Array.from(this.welderIdsTarget.selectedOptions).map(o => o.value)
    }

    return {
      area: this.areaTarget.value,
      weld_no: this.weldNoTarget.value,
      diameter: this.diameterTarget.value || null,
      wall_thickness: this.wallThicknessTarget.value || null,
      image_ref: this.imageRefTarget.value || null,
      welder: welderIds,
      radiography_exposure_id: this.radiographyExposureIdTarget.value || null
    }
  }

  /**
   * Populate form with weld data
   */
  populateForm(weld) {
    this.areaTarget.value = weld.area || ''
    this.weldNoTarget.value = weld.weld_no || ''
    this.diameterTarget.value = weld.diameter || ''
    this.wallThicknessTarget.value = weld.wall_thickness || ''
    this.imageRefTarget.value = weld.image_ref || ''

    // Update radiography exposure Select2
    this.updateSelect2(this.radiographyExposureIdTarget, weld.radiography_exposure_id)

    // Update welder Select2 (multiple)
    if (weld.welder) {
      const welderIds = Array.isArray(weld.welder) ? weld.welder : [weld.welder]
      this.updateSelect2Multiple(this.welderIdsTarget, welderIds)
    }
  }

  /**
   * Clear form fields
   */
  clearForm() {
    this.areaTarget.value = ''
    this.weldNoTarget.value = ''
    this.diameterTarget.value = ''
    this.wallThicknessTarget.value = ''
    this.imageRefTarget.value = ''

    // Clear Select2 dropdowns
    this.updateSelect2(this.radiographyExposureIdTarget, '')
    this.updateSelect2Multiple(this.welderIdsTarget, [])

    this.hideError()
  }

  /**
   * Validate required fields
   */
  validateFields() {
    // At least weld number should be filled
    if (!this.weldNoTarget.value.trim()) {
      this.showError('Weld number is required')
      this.weldNoTarget.focus()
      return false
    }

    // Check if exposure setup is selected
    if (!this.radiographyExposureIdTarget.value) {
      this.showError('Exposure setup is required')
      return false
    }

    return true
  }

  /**
   * Handle successful save
   */
  handleSaveSuccess(data) {
    console.log('[rt-welds] Save success, updating DOM')

    const weld = data.weld

    // Update or create weld row in DOM
    this.updateWeldInDOM(weld)

    // Clear form
    this.clearForm()
    this.isEditMode = false
    this.weldIdTarget.value = ''
    this.saveButtonTextTarget.textContent = 'Save Weld'

    // Scroll to the saved weld and highlight it
    const weldRow = this.weldsContainerTarget.querySelector(`tr[data-weld-id="${weld.id}"]`)
    if (weldRow) {
      weldRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      // Highlight the row briefly with green background
      weldRow.style.backgroundColor = '#d4edda'
      setTimeout(() => {
        weldRow.style.backgroundColor = ''
      }, 2000)
    }
  }

  /**
   * Update or create weld row in DOM
   */
  updateWeldInDOM(weld) {
    const existingRow = this.weldsContainerTarget.querySelector(`tr[data-weld-id="${weld.id}"]`)

    // Remove empty state if it exists
    const emptyState = this.weldsContainerTarget.querySelector('.text-center.text-muted')
    if (emptyState) {
      emptyState.remove()
    }

    // Ensure table structure exists
    if (!this.weldsContainerTarget.querySelector('table')) {
      this.createTableStructure()
    }

    const rowHtml = this.generateWeldRowHtml(weld)

    if (existingRow) {
      // Update existing row
      existingRow.outerHTML = rowHtml
    } else {
      // Add new row at the end of tbody
      const tbody = this.weldsContainerTarget.querySelector('tbody')
      if (tbody) {
        tbody.insertAdjacentHTML('beforeend', rowHtml)
      }
    }
  }

  /**
   * Create table structure when adding first weld
   */
  createTableStructure() {
    // Get the optional header title from the form label
    const optionalHeaderLabel = this.element.querySelector('label.font-weight-bold')
    const optionalHeaderTitle = optionalHeaderLabel ? optionalHeaderLabel.textContent.trim() : 'Optional Header'

    const tableHtml = `
      <div class="table-responsive">
        <table class="table table-hover table-sm">
          <thead>
            <tr>
              <th>NVI ID</th>
              <th>${optionalHeaderTitle}</th>
              <th>Weld No.</th>
              <th>Diameter</th>
              <th>Wall Thickness</th>
              <th>Image Ref</th>
              <th>Welder(s)</th>
              <th>Exposure</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
          </tbody>
        </table>
      </div>
    `
    this.weldsContainerTarget.innerHTML = tableHtml
  }

  /**
   * Generate HTML for a weld table row
   */
  generateWeldRowHtml(weld) {
    const welderNames = this.getWelderNames(weld.welder)
    const exposureCode = this.getExposureCode(weld.radiography_exposure_id)

    const diameterDisplay = weld.diameter ? `${weld.diameter}mm` : '-'
    const wallThicknessDisplay = weld.wall_thickness ? `${weld.wall_thickness}mm` : '-'
    const exposureDisplay = exposureCode
      ? `<span class="badge badge-dark">${exposureCode}</span>`
      : '-'

    return `
      <tr data-weld-id="${weld.id}">
        <td>${weld.nvi_w_id || '-'}</td>
        <td>${weld.area || '-'}</td>
        <td>${weld.weld_no || '-'}</td>
        <td>${diameterDisplay}</td>
        <td>${wallThicknessDisplay}</td>
        <td>${weld.image_ref || '-'}</td>
        <td>${welderNames || '-'}</td>
        <td>${exposureDisplay}</td>
        <td class="text-nowrap">
          <button class="btn btn-sm btn-outline-primary mr-1" type="button" data-action="click->rt-welds#editWeld" data-weld-id="${weld.id}" title="Edit">
            <i class="fa fa-edit"></i>
          </button>
          <button class="btn btn-sm btn-outline-danger" type="button" data-action="click->rt-welds#deleteWeld" data-weld-id="${weld.id}" title="Delete">
            <i class="fa fa-trash"></i>
          </button>
        </td>
      </tr>
    `
  }

  /**
   * Get welder names from welder IDs using the welder select dropdown
   */
  getWelderNames(welderIds) {
    if (!welderIds || (Array.isArray(welderIds) && welderIds.length === 0)) {
      return ''
    }

    const ids = Array.isArray(welderIds) ? welderIds : [welderIds]
    const names = []

    // Get names from the welder select dropdown options
    const welderSelect = this.welderIdsTarget
    if (welderSelect) {
      ids.forEach(id => {
        const option = welderSelect.querySelector(`option[value="${id}"]`)
        if (option) {
          names.push(option.textContent.trim())
        }
      })
    }

    return names.join(', ')
  }

  /**
   * Get exposure code from exposure ID using the exposure select dropdown
   */
  getExposureCode(exposureId) {
    if (!exposureId) {
      return ''
    }

    const exposureSelect = this.radiographyExposureIdTarget
    if (exposureSelect) {
      const option = exposureSelect.querySelector(`option[value="${exposureId}"]`)
      if (option) {
        // The option text is in format "SETUP_CODE - Material"
        // Extract just the setup code (before the " - ")
        const text = option.textContent.trim()
        const dashIndex = text.indexOf(' - ')
        return dashIndex > 0 ? text.substring(0, dashIndex) : text
      }
    }

    return ''
  }

  /**
   * Show empty state when no welds exist
   */
  showEmptyState() {
    const tableContainer = this.weldsContainerTarget.querySelector('.table-responsive')
    if (tableContainer) {
      tableContainer.remove()
    }

    this.weldsContainerTarget.innerHTML = `
      <div class="text-center text-muted py-4">
        <i class="fa fa-link mr-2" style="font-size: 48px;"></i>
        <p class="mt-2">No welds added yet</p>
        <p class="small">Add welds using the form above</p>
      </div>
    `
  }

  /**
   * Update Select2 dropdown value
   */
  updateSelect2(element, value) {
    if (!element) return

    element.value = value || ''

    // Update Select2 if initialized
    if (window.jQuery && window.jQuery(element).data('select2')) {
      window.jQuery(element).val(value || '').trigger('change')
    } else if (element.tomselect) {
      element.tomselect.setValue(value || '')
    }
  }

  /**
   * Update Select2 multiple dropdown values
   */
  updateSelect2Multiple(element, values) {
    if (!element) return

    // Update Select2 if initialized
    if (window.jQuery && window.jQuery(element).data('select2')) {
      window.jQuery(element).val(values).trigger('change')
    } else if (element.tomselect) {
      element.tomselect.setValue(values)
    } else {
      // Fallback for native multi-select
      Array.from(element.options).forEach(option => {
        option.selected = values.includes(option.value)
      })
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    if (!this.hasAlertBoxTarget) return

    this.alertBoxTarget.textContent = message
    this.alertBoxTarget.classList.remove('d-none')

    // Auto-hide after 5 seconds
    setTimeout(() => this.hideError(), 5000)
  }

  /**
   * Hide error message
   */
  hideError() {
    if (!this.hasAlertBoxTarget) return
    this.alertBoxTarget.classList.add('d-none')
  }

  /**
   * Get CSRF token
   */
  get csrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }
}
